// Pricing dinamico per prodotto semplice con quantità multipla
// ID prodotto: 61
export const PRODUCT_ID = 61;

// Prezzi dei bundle
const PRICE_SINGLE = 29.90;
const PRICE_BUNDLE_2 = 54.90;
const PRICE_BUNDLE_3 = 74.90;

// Prezzo senza bundle (prezzo regolare)
const REGULAR_PRICE_SINGLE = 39.90;

/**
 * Calcola la combinazione ottimale di bundle per una quantità specifica
 * @param quantity - Quantità totale desiderata
 * @returns Oggetto con la combinazione ottimale (bundle3, bundle2, single) e il totale
 */
export function calculateOptimalBundle(quantity: number): {
  bundle3: number; // Numero di bundle da 3
  bundle2: number; // Numero di bundle da 2
  single: number;  // Numero di prodotti singoli
  total: number;
  totalRegular: number;
} {
  if (quantity <= 0) {
    return { bundle3: 0, bundle2: 0, single: 0, total: 0, totalRegular: 0 };
  }

  // Algoritmo greedy: preferiamo sempre i bundle più grandi (miglior prezzo per unità)
  let remaining = quantity;
  let bundle3 = 0;
  let bundle2 = 0;
  let single = 0;

  // Prendi quanti più bundle da 3 possibile
  bundle3 = Math.floor(remaining / 3);
  remaining = remaining % 3;

  // Se rimane 1, meglio convertire un bundle da 3 in bundle da 2 + singolo
  // (2+1 = 3, ma costa meno: 54.90 + 29.90 = 84.80 vs 74.90)
  // NO, in realtà 74.90 < 84.80, quindi meglio tenere bundle3 + singolo
  // Se rimane 1, aggiungi un singolo
  if (remaining === 1) {
    single = 1;
  } else if (remaining === 2) {
    // Rimane 2, prendi un bundle da 2
    bundle2 = 1;
  }

  // Calcola totali
  const total = (bundle3 * PRICE_BUNDLE_3) + (bundle2 * PRICE_BUNDLE_2) + (single * PRICE_SINGLE);
  const totalRegular = (bundle3 * 89.70) + (bundle2 * 59.80) + (single * REGULAR_PRICE_SINGLE);

  return { bundle3, bundle2, single, total, totalRegular };
}

/**
 * Calcola il prezzo dinamico in base alla quantità
 * @param quantity - Quantità selezionata
 * @returns Oggetto con prezzo e prezzo regolare (se presente)
 */
export function getDynamicPrice(quantity: number): { price: string; regular_price?: string; breakdown?: string } {
  // Quantità 1, 2, 3 sono dirette
  switch (quantity) {
    case 1:
      return { price: '29.90', regular_price: '39.90' };
    case 2:
      return { price: '54.90', regular_price: '59.80' };
    case 3:
      return { price: '74.90', regular_price: '89.70' };
    default:
      // Per quantità > 3, calcola combinazione ottimale
      const optimal = calculateOptimalBundle(quantity);
      const parts: string[] = [];
      if (optimal.bundle3 > 0) {
        parts.push(`${optimal.bundle3} ${optimal.bundle3 === 1 ? 'bundle da 3' : 'bundle da 3'}`);
      }
      if (optimal.bundle2 > 0) {
        parts.push(`${optimal.bundle2} ${optimal.bundle2 === 1 ? 'bundle da 2' : 'bundle da 2'}`);
      }
      if (optimal.single > 0) {
        parts.push(`${optimal.single} ${optimal.single === 1 ? 'singolo' : 'singoli'}`);
      }
      const breakdown = parts.join(' + ');
      
      return {
        price: optimal.total.toFixed(2),
        regular_price: optimal.totalRegular.toFixed(2),
        breakdown: breakdown
      };
  }
}

/**
 * Calcola il totale per una quantità specifica
 * @param quantity - Quantità selezionata
 * @returns Totale in formato numero
 */
export function calculateDynamicTotal(quantity: number): number {
  if (quantity <= 3) {
    const pricing = getDynamicPrice(quantity);
    return parseFloat(pricing.price);
  }
  const optimal = calculateOptimalBundle(quantity);
  return optimal.total;
}

/**
 * Ottiene la combinazione ottimale per aggiungere al carrello
 * @param quantity - Quantità totale desiderata
 * @returns Array di oggetti {id: number, quantity: number} da aggiungere al carrello
 */
export function getCartItemsForQuantity(quantity: number): Array<{id: number, quantity: number}> {
  if (quantity <= 3) {
    // Per quantità 1-3, usa il prodotto singolo con quella quantità
    return [{ id: PRODUCT_ID, quantity: quantity }];
  }

  // Per quantità > 3, calcola combinazione ottimale
  const optimal = calculateOptimalBundle(quantity);
  const items: Array<{id: number, quantity: number}> = [];

  // Aggiungi bundle da 3 (ogni bundle da 3 = 1 prodotto con quantity 3)
  for (let i = 0; i < optimal.bundle3; i++) {
    items.push({ id: PRODUCT_ID, quantity: 3 });
  }

  // Aggiungi bundle da 2
  for (let i = 0; i < optimal.bundle2; i++) {
    items.push({ id: PRODUCT_ID, quantity: 2 });
  }

  // Aggiungi singoli
  for (let i = 0; i < optimal.single; i++) {
    items.push({ id: PRODUCT_ID, quantity: 1 });
  }

  return items;
}

